import { ProfileAdd, Tag } from 'iconsax-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteLoaderData } from 'react-router';
import ProfileAdd2 from '@/assets/images/icons/profile-add-multiple.svg?react';
import AppCell from '@/components/common/app-cell';
import SearchInput from '@/components/common/search-input';
import { ContactList, type ContactListRef } from '@/components/contacts/contact-list/contact-list';
import AppHeaderMenu from '@/components/layouts/page-layout/app-header-menu';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useFriendRequestCount from '@/hooks/contacts/use-friend-request-count';
import useSortedFriends from '@/hooks/contacts/use-sorted-friends';
import useUserPlatform from '@/hooks/use-user-platform';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';

export default function ContactIndexRoute() {
  const routeLoaderData = useRouteLoaderData<PromiseLoaderReturnType>(ROUTES.CONTACT.$path());
  const { t } = useTranslation();
  const groupListRef = useRef<ContactListRef>(null);
  const { sortedFriends } = useSortedFriends();
  const { friendRequestCount } = useFriendRequestCount();
  const { isEmbedded } = useUserPlatform();
  const { isDesktop } = useUserPlatform();
  return (
    <PageLayout
      title={t('nav.contact')}
      className='gap-0 pb-safe-offset-16 desktop:pb-0 desktop:border-r desktop:border-neutral-300'
      headerConfig={{
        actions: <AppHeaderMenu />,
        showHeader: !isEmbedded,
        elementAfterNav: (
          <Link
            className='mx-3 block'
            to={ROUTES.SEARCH.$path()}>
            <SearchInput
              className='pointer-events-none'
              keyword=''
            />
          </Link>
        ),
      }}>
      <Link to={ROUTES.CONTACT.USERS.APPLICATIONS.$path()}>
        <AppCell
          label={t('contact.new-friends')}
          className='min-h-12 desktop:px-10'
          prefix={<ProfileAdd size={20} />}
          chevron
          suffix={
            friendRequestCount > 0 ? (
              <span className='badge bg-red-500 text-neutral-0 text-[0.625rem] badge-sm px-1 outline1 outline-white'>
                {friendRequestCount}
              </span>
            ) : undefined
          }
        />
      </Link>

      <Link to={ROUTES.CONTACT.GROUPS.$path()}>
        <AppCell
          chevron
          label={t('contact.groups.title')}
          className='min-h-12 desktop:px-10'
          prefix={<ProfileAdd2 className='aspect-square size-5' />}
        />
      </Link>
      {!isDesktop && (
        <Link to={ROUTES.CONTACT.TAGS.$path()}>
          <AppCell
            chevron
            label={t('common.tag')}
            className='min-h-12 desktop:px-10'
            prefix={<Tag className='aspect-square size-5' />}
          />
        </Link>
      )}
      <LoadingLayout
        overlay
        promise={routeLoaderData?.promises}>
        {() => (
          <ContactList
            ref={groupListRef}
            friends={sortedFriends}
            itemClassName='desktop:px-5'
          />
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
